import MessageTokenExpired from "@src/components/messages/token-expired";
import Modal from "@src/components/modal";
import { useLazyGetUserReportsQuery } from "@src/services/reports";
import { loginActions } from "@src/store/slices/login";
import {
  selectAuthExpireError,
  selectAuthRedirectToLogin,
  selectAuthToken,
} from "@src/store/slices/login/selectors";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import FullScreenLoading from "../FullScreenLoading";
import ViewMode from "../ViewMode";

import { AnalyticsProvider } from "@src/contexts/AnalyticsProvider";
import { useBroadcastLogout } from "@src/hooks/useBroadcastLogout";
import { useViewMode } from "@src/hooks/useViewMode";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";

const Menu = dynamic(() => import("../Menu"), { ssr: false });

export default function Layout({ children }: Readonly<{ children: React.ReactNode }>) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [triggerGetUserReports, { data }] = useLazyGetUserReportsQuery();
  const { reports, externalWorkspaceId, solutions } = data || { reports: [] } || { solutions: [] };
  const authToken = useAppSelector(selectAuthToken);
  const error = useAppSelector(selectAuthExpireError);
  const isAuth = !!authToken && !error;
  const redirectToLogin = useAppSelector(selectAuthRedirectToLogin);
  const isViewMode = useViewMode();

  useBroadcastLogout();
  useViewMode();

  useEffect(() => {
    if (authToken) {
      triggerGetUserReports({});
    }
  }, [authToken, isViewMode]);

  useEffect(() => {
    if (redirectToLogin || !isAuth) {
      router.push("/login");
    } else if (router.asPath === "/") {
      router.push("/home");
    }

    return () => {
      dispatch(loginActions.controlView({ redirectToLogin: false }));
    };
  }, [isAuth, redirectToLogin, router]);

  useEffect(() => {
    const { slugs } = router.query;
    if (router.asPath !== "/" && !slugs) return;

    const [workspaceId, reportId] = Array.isArray(slugs) ? slugs : [];

    const [report] = reports || [];

    if (!workspaceId && !reportId) {
      if (!report) return;
      const { externalId } = report;
      router.push(`/${externalWorkspaceId}/${externalId}`);
    }
  }, [router, router.query, reports, externalWorkspaceId]);

  return (
    <AnalyticsProvider>
      <section className={`flex flex-col justify-between w-full min-h-full bg-body `}>
        {isAuth ? (
          <>
            <div className="z-50">
              <ViewMode />
            </div>
            <div className="z-40">
              <Menu reports={reports} workspaceId={externalWorkspaceId} solutions={solutions} />
            </div>
            <div className="overflow-auto ml-[88px]">
              {React.cloneElement(children as React.ReactElement<any>, { reports })}
            </div>
            {error ? (
              <Modal isCloseIcon={false} width="w-[400px]">
                <MessageTokenExpired />
              </Modal>
            ) : null}
          </>
        ) : (
          <FullScreenLoading blurryBackground={false} width={60} height={60} />
        )}
      </section>
    </AnalyticsProvider>
  );
}
